/**
 * Generates a Zod schema from resolved field config.
 * Used with react-hook-form + zodResolver for config-driven validation.
 */
import { z } from "zod";
import { evaluateCondition } from "./conditionUtils";
import type { FieldConfig, ValidationRule } from "./types";

/** Nested shapes for known composite fields */
const registeredAddressSchema = z.object({
	address1: z.string().min(1, "Address line 1 is required"),
	address2: z.string().optional(),
	city: z.string().min(1, "City is required"),
	stateProvince: z.string().min(1, "State/Province is required"),
	zip: z.string().min(1, "ZIP/Postal code is required"),
	country: z.string().min(1, "Country is required"),
});

const kycContactSchema = z.object({
	name: z.string().min(1, "Name is required"),
	email: z.string().min(1, "Email is required").email("Invalid email"),
	phone: z.string().min(1, "Phone is required"),
	role: z.string().optional(),
});

function shouldApplyRule(
	rule: ValidationRule,
	formValues: Record<string, unknown>,
): boolean {
	if (!rule.appliesWhen) return true;
	return evaluateCondition(rule.appliesWhen, formValues);
}

/**
 * Maps a scalar FieldConfig to a Zod schema.
 * formValues is used for appliesWhen conditions.
 */
function fieldConfigToZod(
	config: FieldConfig,
	formValues: Record<string, unknown>,
): z.ZodType<unknown> {
	let base: z.ZodType<unknown>;

	switch (config.type) {
		case "boolean":
			base = z.coerce.boolean();
			break;
		case "number":
			base = z.coerce.number();
			break;
		default:
			base = z.string();
	}

	const isStringType =
		config.type === "text" ||
		config.type === "textarea" ||
		config.type === "email" ||
		config.type === "url" ||
		config.type === "phone" ||
		config.type === "file" ||
		config.type === "date";

	// Collect required rule for string types — apply last so base stays ZodString for min_length, etc.
	let requiredRule: ValidationRule | null = null;

	for (const rule of config.validationRules) {
		if (!shouldApplyRule(rule, formValues)) continue;

		if (rule.type === "required") {
			if (isStringType) {
				requiredRule = rule;
				continue;
			}
			base = base.refine((v) => v !== undefined && v !== null && v !== "", {
				message: rule.message,
			});
			continue;
		}

		switch (rule.type) {
			case "min_length":
				if (isStringType) {
					base = (base as z.ZodString).min(rule.value as number, rule.message);
				}
				break;
			case "max_length":
				if (isStringType) {
					base = (base as z.ZodString).max(rule.value as number, rule.message);
				}
				break;
			case "email":
				if (isStringType) {
					base = (base as z.ZodString).email(rule.message);
				}
				break;
			case "url":
				if (isStringType) {
					base = (base as z.ZodString).regex(/^https?:\/\/.+/, rule.message);
				}
				break;
			case "regex":
				if (rule.value && typeof rule.value === "string" && isStringType) {
					try {
						base = (base as z.ZodString).regex(
							new RegExp(rule.value),
							rule.message,
						);
					} catch {
						// invalid regex, skip
					}
				}
				break;
			case "min":
			case "max": {
				const v = rule.value as number;
				if (config.type === "number") {
					base = (base as z.ZodNumber).refine(
						(n) => (rule.type === "min" ? n >= v : n <= v),
						{ message: rule.message },
					);
				}
				break;
			}
			default:
				break;
		}
	}

	// Apply required for string types last: coerce undefined to "" then validate min(1)
	if (requiredRule && isStringType) {
		base = (base as z.ZodString)
			.transform((v) => v ?? "")
			.pipe(z.string().min(1, requiredRule.message));
	}

	if (!config.required) {
		base = base.optional().or(z.literal(""));
	}

	return base;
}

/**
 * Builds a Zod schema from resolved fields.
 * Filters by dependsOn using formValues.
 */
export function generateZodSchemaFromFieldConfig(
	fields: FieldConfig[],
	formValues: Record<string, unknown>,
): z.ZodObject<z.ZodRawShape> {
	const shape: Record<string, z.ZodType<unknown>> = {};

	const visibleFields = fields.filter((f) => {
		if (!f.dependsOn) return true;
		return evaluateCondition(f.dependsOn, formValues);
	});

	const compositeIds = new Set([
		"registeredAddress",
		"primaryBusinessContact",
		"primaryProductContact",
		"primaryLegalContact",
		"primarySigner",
		"complianceOfficer",
		"ownershipTiers",
		"licenses",
		"boardOfDirectors",
	]);

	for (const field of visibleFields) {
		if (compositeIds.has(field.fieldId)) continue;
		shape[field.fieldId] = fieldConfigToZod(field, formValues);
	}

	shape.registeredAddress = registeredAddressSchema;
	shape.primaryBusinessContact = kycContactSchema;
	shape.primaryProductContact = kycContactSchema;
	shape.primaryLegalContact = kycContactSchema;
	shape.primarySigner = kycContactSchema;
	shape.complianceOfficer = kycContactSchema.extend({
		photoIdUrl: z.string().optional(),
	});
	shape.ownershipTiers = z
		.array(z.record(z.string(), z.unknown()))
		.optional()
		.refine(
			(tiers) => {
				if (!tiers || tiers.length === 0) return true;
				const uboTrigger = 25;
				for (const tier of tiers as Record<string, unknown>[]) {
					const ownerType = tier.ownerType ?? tier.owner_type;
					const pct = tier.ownershipPercentage ?? tier.ownership_percentage;
					if (!ownerType || pct === undefined || pct === null) return false;
					if (
						ownerType === "Individual" &&
						typeof pct === "number" &&
						pct >= uboTrigger
					) {
						const uboFields = [
							tier.fullName ?? tier.full_name,
							tier.dateOfBirth ?? tier.date_of_birth,
							tier.nationality,
							tier.residentialAddress ?? tier.residential_address,
							tier.photoIdUrl ?? tier.photo_id_url,
						];
						if (uboFields.some((v) => !v || String(v).trim() === ""))
							return false;
					}
				}
				return true;
			},
			{
				message:
					"Complete ownership tier details (owner type, percentage, UBO info when ≥25%)",
			},
		);
	shape.licenses = z.array(z.record(z.string(), z.unknown())).optional();
	shape.boardOfDirectors = z
		.array(z.record(z.string(), z.unknown()))
		.optional();

	return z.object(shape).passthrough();
}
